/**
 * AppShell: the chrome of every signed-in page (UI_SDD §R.2, DEC-061, DEC-194).
 *
 * 248px left nav + 54px top bar. Pages render inside the scrollable content area; a
 * scan page also gets the scan header with its tabs (`ScanHeader`) above it.
 *
 * Workspace display is static single-workspace (DEC-061): the tenant name is shown with
 * no switcher affordance because multi-tenancy has no backend.
 *
 * DEC-194 decision 1: the sidebar never changes with the page. New scan, Scans (live
 * badge), Debt trend, then "Recently opened": up to 3 scans this user opened, remembered
 * per browser. Settings left the list for the user menu at the bottom (DEC-198). Before DEC-194 the scan's pages lived in an in-scan
 * block gated on the scan being loaded, so they vanished on Scans, Debt trend and
 * Settings, and nothing remembered the scan. The scan's pages are now tabs in the scan
 * header (decision 2), which also replaces the report lens pill of the top bar
 * (decision 4).
 */
import { useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Layers, Plus, Radar, TrendingUp, X } from 'lucide-react'
import { ThemeToggle } from '../shared/ThemeToggle'
import { PillDot } from '../shared/Badge'
import { scansApi } from '../../api/scans'
import { ApiError } from '../../api/client'
import { useLiveScans } from '../../hooks/useScanQueries'
import { SCAN_STATUS, liveBadgeText, statusOf } from '../scans/scanStatus'
import { dateLabel, sourceLabel } from '../scans/scanFormat'
import { useAuth } from '../../hooks/useAuth'
import type { ScanStatusResponse } from '../../types/api'
import { repoLabel } from '../../utils/format'
import { ScanHeader } from './ScanHeader'
import { UserMenu } from './UserMenu'
import { landingPath, tabFromPath, type ScanNavStatus, type ScanTabId } from './scanTabs'
import {
  FROM_RECENT,
  cameFromRecent,
  enterScan,
  markGone,
  recentKey,
  recordVisit,
  removeRecent,
  useRecentScans,
  type RecentScan,
} from './recentScans'

// ---------------------------------------------------------------------------
// Profiles and lenses
// ---------------------------------------------------------------------------

/** The report views a tab can show, with the lens name an admin reads them as. */
const VIEW_LENS: Partial<Record<ScanTabId, string>> = {
  engineering: 'Engineering & IT',
  executive: 'Executive',
  product: 'Product & GTM',
}

/** A missing scan is not retried: a 404 is an answer, not a hiccup. */
function retryUnlessMissing(count: number, err: unknown): boolean {
  if (err instanceof ApiError && err.status === 404) return false
  return count < 3
}

function isMissing(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404
}

function navStatusOf(scan: ScanStatusResponse): ScanNavStatus {
  return scan.deleted_at ? 'deleted' : statusOf(scan)
}

const STATUS_WORD = (s: ScanNavStatus | null): string =>
  s === 'deleted' ? 'Deleted permanently' : s ? SCAN_STATUS[s].word : ''

// ---------------------------------------------------------------------------
// Shell
// ---------------------------------------------------------------------------

interface AppShellProps {
  scanId?: string
  /** The page's name, shown on the left of the top bar. */
  crumb: string
  /** Which sidebar item is active. `new` is the New scan form; `monitor` and `report`
   *  are scan pages, which light up the scan in Recently opened instead. */
  active: 'scans' | 'new' | 'trends' | 'settings' | 'monitor' | 'report' | 'super-admin'
  /** Extra content rendered on the right of the top bar. */
  topBarActions?: React.ReactNode
  children: React.ReactNode
}

export function AppShell({ scanId, crumb, active, topBarActions, children }: AppShellProps) {
  const navigate = useNavigate()
  const location = useLocation()

  const { data: scan, error: scanError } = useQuery({
    queryKey: ['scan', scanId],
    queryFn: () => scansApi.get(scanId!),
    enabled: !!scanId,
    retry: retryUnlessMissing,
  })
  const scanMissing = !!scanId && isMissing(scanError)

  // One shared, self-stopping poll (DEC-191 decision 5): it runs only while a scan is
  // live, and every scans query is invalidated when a scan's stream ends.
  const { data: live } = useLiveScans()
  const badge = live ? liveBadgeText(live) : null

  // Profile gates are a UX convenience only (DEC-085, DEC-179): profile=null
  // (bootstrap/OAuth tokens) and profile=admin see everything; the backend enforces the
  // real gate on every request.
  const { claims } = useAuth()
  const profile = claims?.profile ?? null

  // ── Recently opened (DEC-194 decisions 1 and 3) ──────────────────────────
  const key = recentKey(claims)
  const recent = useRecentScans(key)
  const here = tabFromPath(location.pathname)
  const currentTab: ScanTabId | null = here && here.scanId === scanId ? here.tab : null

  // Opening any scan page records the visit and the tab. A scan opened from elsewhere
  // goes first; clicking it in Recently opened, or moving between its tabs, leaves it
  // where it is, so the list never shifts under the pointer.
  const navState = location.state
  useEffect(() => {
    if (!scanId) {
      enterScan(null)
      return
    }
    const promote = enterScan(scanId) && !cameFromRecent(navState)
    if (scanMissing) {
      markGone(key, scanId)
      return
    }
    const snapshot = scan
      ? { repo: repoLabel(scan.repo_url), source: sourceLabel(scan.repo_url, scan.branch), status: navStatusOf(scan) }
      : undefined
    recordVisit(key, scanId, currentTab, snapshot, promote)
  }, [key, scanId, currentTab, scan, scanMissing, navState])

  // Fresh status for the remembered scans, drawn from the snapshot until it arrives, so
  // nothing in the sidebar waits for a scan to load (decision 4).
  const recentQueries = useQueries({
    queries: recent.items.map((item) => ({
      queryKey: ['scan', item.id],
      queryFn: () => scansApi.get(item.id),
      retry: retryUnlessMissing,
      staleTime: 30_000,
    })),
  })
  const recentRows = recent.items.map((item, n) => {
    const q = recentQueries[n]
    // A refetch that fails keeps the old data, so a 404 is read first.
    const status: ScanNavStatus | null = isMissing(q?.error) ? 'deleted' : q?.data ? navStatusOf(q.data) : item.status
    return { item, status }
  })

  // A remembered scan found gone is marked so, and stays marked offline too.
  const goneIds = recentRows.filter((r) => r.status === 'deleted' && r.item.status !== 'deleted').map((r) => r.item.id).join(',')
  useEffect(() => {
    if (goneIds) goneIds.split(',').forEach((id) => markGone(key, id))
  }, [goneIds, key])

  // ── The scan header ──────────────────────────────────────────────────────
  const remembered = scanId ? recent.items.find((i) => i.id === scanId) : undefined
  const headerStatus: ScanNavStatus | null = scanMissing ? 'deleted' : scan ? navStatusOf(scan) : remembered?.status ?? null
  const headerRepo = scan ? repoLabel(scan.repo_url) : remembered?.repo ?? null
  const headerSource = scan ? sourceLabel(scan.repo_url, scan.branch) : remembered?.source ?? null

  // An admin reading another profile's view is told so [DEC-179]: a borrowed view never
  // looks like your own. Only admins can borrow one; a token with no profile has no
  // view of its own to compare with.
  const viewingAs = profile === 'admin' && currentTab ? VIEW_LENS[currentTab] : undefined

  const navItem = (on: boolean) =>
    `flex items-center gap-2 rounded-sm px-2 py-2 text-[12px] font-semibold transition-colors ${
      on ? 'bg-nav-active text-nav-on' : 'text-nav-body hover:bg-nav-hover hover:text-nav-ink'
    }`

  return (
    <div className="flex h-screen min-h-[600px] bg-ground text-ink overflow-hidden">
      {/* ── Left nav ─────────────────────────────────────────────── */}
      {/* bg-nav: #1A1A1A on light (DEC-193), #1B1628 on dark. Everything inside reads
          the nav-* tokens, never the page's ink/body/faint, since the page is light. */}
      <nav aria-label="Main" className="w-[248px] flex-none bg-nav text-nav-ink border-r border-nav-line flex flex-col h-full">
        {/* Logo */}
        <div className="px-[18px] pt-[18px] pb-3 flex items-center gap-[9px]">
          <div className="w-[26px] h-[26px] rounded-sm bg-accent flex items-center justify-center flex-none">
            <Radar className="w-[15px] h-[15px] text-white" />
          </div>
          <div>
            <div className="font-semibold text-[14px] leading-none tracking-tight">TDIA</div>
            <div className="font-semibold text-[8px] leading-none text-nav-faint mt-[3px] tracking-[2.2px] uppercase">
              AI Factory
            </div>
          </div>
        </div>

        {/* Workspace: static single-workspace display (DEC-061) */}
        <div className="px-3 pb-[10px]">
          <div className="bg-nav-well border border-nav-line rounded-lg px-[11px] py-2 flex items-center gap-2">
            <div className="w-[18px] h-[18px] rounded-sm bg-nav-chip text-nav-ink text-[9px] font-semibold flex items-center justify-center flex-none">
              W
            </div>
            <div className="font-medium text-[12px] leading-none">Workspace</div>
          </div>
        </div>

        {/* New scan */}
        <div className="px-3 pb-2">
          <button
            onClick={() => navigate('/connect')}
            aria-current={active === 'new' ? 'page' : undefined}
            className="w-full flex items-center gap-2 font-semibold text-[12px] text-nav-accent bg-nav-active border border-dashed border-nav-dash rounded-md px-3 py-[9px] hover:bg-nav-hover transition-colors"
          >
            <Plus className="w-[14px] h-[14px]" /> New scan
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-[6px]">
          {/* Scans dashboard (DEC-191), with a live badge while anything runs or waits */}
          <Link to="/scans" aria-current={active === 'scans' ? 'page' : undefined} className={navItem(active === 'scans')}>
            <Layers className="w-4 h-4 flex-none" />
            <span className="flex-1 min-w-0">Scans</span>
            {badge && (
              <span
                data-testid="scans-live-badge"
                className="ml-auto flex items-center gap-[5px] whitespace-nowrap rounded-full bg-nav-active px-2 py-[2px] text-[10px] font-semibold tabular-nums text-nav-on"
              >
                {live && live.running > 0 && (
                  <span className="w-[6px] h-[6px] rounded-full bg-nav-accent animate-pulse" />
                )}
                {badge}
              </span>
            )}
          </Link>

          {/* Debt trend: workspace-scoped, so it belongs to no single scan (UI_SDD §R.9). */}
          <Link to="/trends" aria-current={active === 'trends' ? 'page' : undefined} className={navItem(active === 'trends')}>
            <TrendingUp className="w-4 h-4 flex-none" />
            <span>Debt trend</span>
          </Link>

          {/* Recently opened (DEC-194 decision 1) */}
          <div className="border-t border-nav-line mt-2 mb-1" />
          <div
            id="recently-opened-label"
            className="font-semibold text-[9px] leading-none tracking-[2.4px] uppercase text-nav-faint px-2 pt-[10px] pb-2"
          >
            Recently opened
          </div>
          {recentRows.length === 0 ? (
            <p data-testid="recent-empty" className="px-2 py-1 text-[11px] leading-[1.45] text-nav-body">
              Scans you open appear here.
            </p>
          ) : (
            <ul aria-labelledby="recently-opened-label" data-testid="recently-opened" className="flex flex-col gap-[2px]">
              {recentRows.map(({ item, status }) => (
                <RecentItem
                  key={item.id}
                  item={item}
                  status={status}
                  to={landingPath(item.id, profile, status, recent.lastTab[item.id])}
                  current={item.id === scanId}
                  onRemove={() => removeRecent(key, item.id)}
                />
              ))}
            </ul>
          )}
        </div>

        {/* User menu (DEC-198): your name opens Settings, Workspace settings (admins),
            Theme and Log out, like the Claude app. Before DEC-140 this footer said
            "Signed in" with no name and no sign-out. */}
        <UserMenu active={active === 'settings' || active === 'super-admin'} />
      </nav>

      {/* ── Main ─────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col h-full min-w-0">
        {/* Top bar */}
        <div className="h-[54px] flex-none bg-glass backdrop-blur-xl border-b border-hairline flex items-center justify-between px-6 relative z-20">
          <div className="flex items-center gap-[9px] min-w-0">
            <span className="font-medium text-[12px] leading-none text-muted truncate">{crumb}</span>
          </div>

          <div className="flex items-center gap-[10px] relative">
            {/* The theme is reachable on every screen, for every profile (DEC-130). */}
            <ThemeToggle />
            {topBarActions}
            {viewingAs && (
              <span
                data-testid="viewing-as"
                className="inline-flex items-center gap-[6px] whitespace-nowrap rounded-full border border-hairline-strong py-[2px] pl-2 pr-[10px] text-[12px] font-medium text-ink"
              >
                <PillDot tone="running" />
                Viewing as {viewingAs}
              </span>
            )}
          </div>
        </div>

        {scanId && (
          <ScanHeader
            scanId={scanId}
            repo={headerRepo}
            source={headerSource}
            status={headerStatus}
            profile={profile}
            current={currentTab}
            visibility={scan?.visibility ?? null}
            isOwner={
              scan?.owned_by_viewer ?? (!!scan?.created_by_user_id && scan.created_by_user_id === claims?.user_id)
            }
            workspaceName={claims?.tenant_name}
            pushedBy={scan?.pushed_by ?? null}
          />
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0 relative">
          {scanMissing ? (
            <MissingScanNotice onBack={() => navigate('/scans')} />
          ) : scanId && scan?.deleted_at ? (
            <DeletedScanNotice scan={scan} onBack={() => navigate('/scans')} />
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  )
}

/** One remembered scan: a link to the tab last used there, and a labelled remove. */
function RecentItem({
  item,
  status,
  to,
  current,
  onRemove,
}: {
  item: RecentScan
  status: ScanNavStatus | null
  to: string
  current: boolean
  onRemove: () => void
}) {
  const word = STATUS_WORD(status)
  const tone = status === 'deleted' || !status ? 'stopped' : SCAN_STATUS[status].tone
  return (
    <li
      data-testid="recent-item"
      data-scan-id={item.id}
      data-status={status ?? ''}
      className={`group flex items-center rounded-sm transition-colors ${current ? 'bg-nav-active' : 'hover:bg-nav-hover'}`}
    >
      <Link
        to={to}
        state={FROM_RECENT}
        aria-current={current ? 'page' : undefined}
        className={`flex min-w-0 flex-1 items-center gap-[9px] rounded-sm px-2 py-[7px] ${current ? 'text-nav-on' : 'text-nav-ink'}`}
      >
        <PillDot tone={tone} pulse={status === 'running'} />
        <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <span className="truncate text-[12px] font-semibold leading-tight">{item.repo}</span>
          <span className="truncate text-[10px] leading-tight text-nav-body">
            {[item.source, word].filter(Boolean).join(' · ')}
          </span>
        </span>
      </Link>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${item.repo} from Recently opened`}
        title="Remove from Recently opened"
        className="mr-1 flex-none rounded-sm p-1 text-nav-muted opacity-60 transition-opacity hover:text-nav-ink hover:opacity-100 focus-visible:opacity-100 group-hover:opacity-100"
      >
        <X className="h-3 w-3" />
      </button>
    </li>
  )
}

/**
 * A page of a scan deleted permanently (DEC-192 decision 3). One place for every scan
 * page, because every one of them renders inside this shell with the scan loaded. The
 * report routes answer 410 for such a scan, so the page under it would only show an
 * error.
 */
function DeletedScanNotice({ scan, onBack }: { scan: ScanStatusResponse; onBack: () => void }) {
  const n = scan.deleted_findings_count ?? 0
  return (
    <div data-testid="deleted-scan-notice" className="mx-auto flex max-w-[620px] flex-col items-center gap-3 px-6 py-16 text-center">
      <span className="text-[20px] font-semibold text-ink">This scan was deleted permanently</span>
      <span className="text-[14px] leading-[1.55] text-body">
        Deleted on {scan.deleted_at ? dateLabel(scan.deleted_at) : ''} by {scan.deleted_by_name ?? 'a workspace member'}. Its{' '}
        {n} {n === 1 ? 'finding was' : 'findings were'} erased, so there are no results to open. A record that the scan of{' '}
        {repoLabel(scan.repo_url)} existed stays in Archived on the Scans page.
      </span>
      <BackToScans onBack={onBack} />
    </div>
  )
}

/** A scan the server no longer has (404): a remembered one that was removed. */
function MissingScanNotice({ onBack }: { onBack: () => void }) {
  return (
    <div data-testid="deleted-scan-notice" className="mx-auto flex max-w-[620px] flex-col items-center gap-3 px-6 py-16 text-center">
      <span className="text-[20px] font-semibold text-ink">This scan is not available to you</span>
      <span className="text-[14px] leading-[1.55] text-body">
        It was deleted, or the person who started it made it private. Every scan you can open is on the Scans page.
      </span>
      <BackToScans onBack={onBack} />
    </div>
  )
}

function BackToScans({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="rounded-md border border-hairline-strong px-4 py-[9px] text-[13px] font-semibold text-ink hover:bg-hover-hue"
    >
      Back to Scans
    </button>
  )
}
